// Standardization requests — end users asking to publish ("standardise")
// their saved visuals to the shared navigation menu. Served by the
// assistant-preview router mounted at /api/assistant. All calls FAIL SOFT:
// the Analytics workbench hides the queue entirely when the backend is
// unavailable or the user may not see it (401/403/404), so the tab keeps
// working even without this feature.
import api from './client'

export interface StandardizeRequest {
  id: string
  requester: string
  title: string
  suggested_folder?: string
  note?: string
  status: 'pending' | 'approved' | 'rejected'
  review_note?: string
  created?: string
  reviewed?: string
  recipe?: Record<string, unknown>
}

export interface StandardizeRequestList {
  requests: StandardizeRequest[]
  /** True for reviewers (they see ALL requests and may approve/reject);
      others only see their own submissions. */
  can_review: boolean
}

/**
 * List standardization requests for a project. Returns `null` when the
 * feature is unavailable (endpoint missing, no rights, network error) so
 * callers can hide the queue area without surfacing an error.
 */
export async function listStandardizeRequests(
  project: string,
): Promise<StandardizeRequestList | null> {
  try {
    const res = await api.get('/assistant/standardize-requests', {
      params: { project },
    })
    const data = res.data ?? {}
    return {
      requests: Array.isArray(data.requests) ? (data.requests as StandardizeRequest[]) : [],
      can_review: !!data.can_review,
    }
  } catch {
    return null
  }
}

/**
 * Approve or reject a standardization request (reviewer-gated; the backend
 * returns 409 when the request was already resolved). Errors here are NOT
 * swallowed — the caller shows them (the reviewer explicitly acted).
 */
export async function reviewStandardizeRequest(
  project: string,
  requestId: string,
  approve: boolean,
  opts?: { review_note?: string; obj_name?: string; folder_title?: string },
): Promise<{ success: boolean; request?: StandardizeRequest }> {
  const res = await api.post('/assistant/standardize-review', {
    project,
    request_id: requestId,
    approve,
    ...(opts?.review_note ? { review_note: opts.review_note } : {}),
    ...(opts?.obj_name ? { obj_name: opts.obj_name } : {}),
    ...(opts?.folder_title ? { folder_title: opts.folder_title } : {}),
  })
  return res.data as { success: boolean; request?: StandardizeRequest }
}
